import { Dispatch, SetStateAction, useRef } from "react";
import { filter } from "lodash";
import { Activity } from "../../../@types/activities.ts";
import { Activitystream } from "../../../api/activitystreams.ts";
import EmailRecommendationForm from "../activity/contextMenuForms/EmailRecommendationForm.tsx";
import ActivityPdfModal from "./ActivityPdfModal.tsx";
import { ActivityPdf } from "./useActivityPdf.ts";
import { ShareDialog } from "./useActivityShare.ts";

type ActivityShareDialogsArgs = {
  dialog: ShareDialog;
  setDialog: Dispatch<SetStateAction<ShareDialog>>;
  activity: Activity;
  streamsFromActivity?: Activitystream[];
  pdf: ActivityPdf;
  emailRecommendationCount: { internal: number; external: number };
  emailRecommendationInfo: {
    hasAttachments: boolean;
    totalAttachmentSize: number;
    lastRecommendation?: Activity["email_recommendation"];
  };
  teaser?: string;
};

// Renders the modal that belongs to the current `dialog` state of an
// activity (see useActivityShare)
export default function ActivityShareDialogs({
  dialog,
  setDialog,
  activity,
  streamsFromActivity,
  pdf,
  emailRecommendationCount,
  emailRecommendationInfo,
  teaser,
}: ActivityShareDialogsArgs) {
  // Set when the PDF modal is unmounted because the PDF went out. Its onClose
  // still fires once it is hidden and must not drop a PDF (or close a modal)
  // the user started again in the meantime.
  const pdfDone = useRef(false);

  if (dialog === "pdf") {
    return (
      <ActivityPdfModal
        activity={activity}
        pdf={pdf}
        onDone={() => {
          pdfDone.current = true;
          setDialog(null);
        }}
        onClose={() => {
          if (pdfDone.current) {
            pdfDone.current = false;
            return;
          }
          pdf.reset();
          setDialog((current) => (current === "pdf" ? null : current));
        }}
      />
    );
  }

  if (dialog === "email_internal" || dialog === "email_external") {
    const type = dialog === "email_internal" ? "internal" : "external";

    return (
      <EmailRecommendationForm
        activityId={activity.id}
        type={type}
        emailRecommendationInfo={emailRecommendationInfo}
        emailRecommendationCount={emailRecommendationCount}
        teaser={teaser}
        selectableStreams={
          filter(streamsFromActivity, (stream) =>
            Boolean(stream.can?.[`email_recommend_activity_${type}ly`]),
          ) as Activitystream[]
        }
        close={() => setDialog(null)}
      />
    );
  }

  return null;
}
